import React from "react";
import { Play } from "lucide-react";
import { TicketAttachment } from "../../types/ticket";
import { openViewer, ViewerMedia } from "../../utils/imageViewer";

export const isVideoAttachment = (a: TicketAttachment) =>
  a.contentType?.startsWith("video/") ||
  /\.(mp4|mov|webm|m4v|avi|mkv)(\?|$)/i.test(a.url);

const PlayBadge = () => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
    <div className="w-10 h-10 rounded-full bg-black/50 flex items-center justify-center">
      <Play size={20} className="text-white ml-0.5" fill="white" />
    </div>
  </div>
);

const Thumb: React.FC<{ a: TicketAttachment; onClick: () => void; cover?: boolean }> = ({
  a,
  onClick,
  cover,
}) => {
  const video = isVideoAttachment(a);
  return (
    <div
      className={`relative cursor-pointer overflow-hidden rounded-md ${
        cover ? "w-full h-full" : ""
      }`}
      onClick={onClick}
    >
      {video ? (
        <video
          src={a.url}
          muted
          preload="metadata"
          className={cover ? "w-full h-full object-cover" : "rounded-md max-h-52"}
        />
      ) : (
        <img
          src={a.url}
          alt={a.filename || "attachment"}
          className={cover ? "w-full h-full object-cover" : "rounded-md max-h-52"}
        />
      )}
      {video && <PlayBadge />}
    </div>
  );
};

// AttachmentGallery renders message attachments: a single item inline, or 2+ as
// a compact grid. Tapping any item opens the full-screen viewer at that index,
// swipeable across the whole group.
const AttachmentGallery: React.FC<{ items?: TicketAttachment[] }> = ({
  items,
}) => {
  if (!items || items.length === 0) return null;

  const media: ViewerMedia[] = items.map((a) => ({
    url: a.url,
    type: isVideoAttachment(a) ? "video" : "image",
  }));
  const open = (i: number) => openViewer(media, i);

  if (items.length === 1) {
    return (
      <div className="mt-1">
        <Thumb a={items[0]} onClick={() => open(0)} />
      </div>
    );
  }

  return (
    <div className="mt-1 grid grid-cols-2 gap-1 w-[240px] max-w-full">
      {items.map((a, i) => (
        <div key={i} className="aspect-square">
          <Thumb a={a} onClick={() => open(i)} cover />
        </div>
      ))}
    </div>
  );
};

export default AttachmentGallery;
